/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.

==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return {x: 0, y: 0}
  },
  routes: [

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // User Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard'
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('./views/user/Dashboard.vue'),
          meta: {
            rule: 'visitor',
            authRequired: true
          }
        },
        {
          path: '/learn',
          name: 'learn',
          component: () => import('./views/learn/LearnStarter.vue'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        {
          path: '/activity/flashcards',
          name: 'activity-flashcards',
          component: () => import('./views/activity/flashcard/FlashcardStarter'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        {
          path: '/select-course',
          name: 'course-selector',
          component: () => import('./views/course/course-selector/CourseSelector.vue'),
          meta: {
            rule: 'user',
            pageTitle: 'Course Selector',
            authRequired: true
          }
        },
        {
          path: '/course/:courseId',
          name: 'course-view',
          component: () => import('./views/course/course-view/CourseView'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        // =============================================================================
        // Course Management Routes
        // =============================================================================
        {
          path: '/a/courses',
          name: 'management-list-course',
          component: () => import('./views/management/courses/course-list/ListCourses.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Courses List',
            authRequired: true
          }
        },
        {
          path: '/a/courses/new',
          ame: 'management-create-course',
          component: () => import('./views/management/courses/course-create/CreateCourse.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'New', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'New Course',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId',
          name: 'management-view-course',
          component: () => import('./views/management/courses/course-view/CourseView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Course',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId/subscriptions',
          name: 'management-view-course-subscriptions',
          component: () => import('./views/management/courses/course-view/subscription-list/ListSubscriptions.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Course Subscriptions',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId/edit',
          name: 'management-edit-course',
          component: () => import('./views/management/courses/course-edit/EditCourse.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Course',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId/builder',
          name: 'management-build-course',
          component: () => import('./views/management/courses/course-edit/BuildCourse'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'Build', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Build Course',
            authRequired: true
          }
        },
        // =============================================================================
        // Group Management Routes
        // =============================================================================
        {
          path: '/a/groups',
          name: 'management-list-group',
          component: () => import('./views/management/groups/group-list/ListGroups.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Groups List',
            authRequired: true
          }
        },
        {
          path: '/a/groups/new',
          ame: 'management-create-group',
          component: () => import('./views/management/groups/group-create/CreateGroup.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'New', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'New Group',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId',
          name: 'management-view-group',
          component: () => import('./views/management/groups/group-view/GroupView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Group',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId/subscriptions',
          name: 'management-view-group-subscriptions',
          component: () => import('./views/management/groups/group-view/GroupView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Group Subscriptions',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId/edit',
          name: 'management-edit-group',
          component: () => import('./views/management/groups/group-edit/EditGroup.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Group',
            authRequired: true
          }
        },
        // =============================================================================
        // User Management Routes
        // =============================================================================
        {
          path: '/a/users',
          name: 'management-list-user',
          component: () => import('./views/management/users/user-list/ListUser'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'User List',
            authRequired: true
          }
        },
        {
          path: '/a/users/upload',
          name: 'management-upload-user',
          component: () => import('./views/management/users/user-create/UploadUsers'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'Upload', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Upload Users',
            authRequired: true
          }
        },
        {
          path: '/a/users/:userId',
          name: 'management-view-user',
          component: () => import('./views/management/users/user-view/UserView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View User',
            authRequired: true
          }
        },
        {
          path: '/a/users/:userId/progress/:courseId',
          name: 'management-view-user-course-progress',
          component: () => import('./views/management/users/user-view/UserViewCourseProgress'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'View User Course Progress', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View User Course Progress',
            authRequired: true
          }
        },
        // =============================================================================
        // Domain Management Routes
        // =============================================================================
        {
          path: '/a/domains',
          name: 'management-list-domain',
          component: () => import('./views/management/domains/domain-list/ListDomains.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Domains', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Domains List'
          }
        },
        {
          path: '/a/domains/new',
          name: 'management-create-domain',
          component: () => import('./views/management/domains/domain-create/CreateDomain'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Domains', url: '/a/domains'},
              {title: 'Create', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Create Domain',
            authRequired: true
          }
        },

        {
          path: '/a/domains/:domainId',
          name: 'management-view-domain',
          component: () => import('./views/management/domains/domain-view/DomainView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Domains', url: '/a/domains'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Domain',
            authRequired: true
          }
        },
        {
          path: '/a/domains/:domainId/edit',
          name: 'management-edit-domain',
          component: () => import('./views/management/domains/domain-edit/EditDomain.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Domains', url: '/a/domains'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Domain',
            authRequired: true
          }
        },
        {
          path: '/a/domains/:domainId/builder',
          name: 'management-build-domain',
          component: () => import('./views/management/domains/domain-edit/BuildDomain'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Domains', url: '/a/domains'},
              {title: 'Build', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Build Domain',
            authRequired: true
          }
        },
        // =============================================================================
        // Set Management Routes
        // =============================================================================
        {
          path: '/a/sets',
          name: 'management-list-set',
          component: () => import('./views/management/sets/set-list/ListSets.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Sets', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Sets List',
            authRequired: true
          }
        },
        {
          path: '/a/sets/new',
          ame: 'management-create-set',
          component: () => import('./views/management/sets/set-create/CreateSet.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Sets', url: '/a/sets'},
              {title: 'New', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'New Set',
            authRequired: true
          }
        },
        {
          path: '/a/sets/:setId',
          name: 'management-view-set',
          component: () => import('./views/management/sets/set-view/SetView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Sets', url: '/a/sets'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Set',
            authRequired: true
          }
        },
        {
          path: '/a/sets/:setId/edit',
          name: 'management-edit-set',
          component: () => import('./views/management/sets/set-edit/EditSet.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Sets', url: '/a/sets'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Set',
            authRequired: true
          }
        },
        {
          path: '/a/sets/:setId/builder',
          name: 'management-build-set',
          component: () => import('./views/management/sets/set-edit/BuildSet'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Sets', url: '/a/sets'},
              {title: 'Build', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Build Set',
            authRequired: true
          }
        },
        // =============================================================================
        // Lesson Management Routes
        // =============================================================================
        {
          path: '/a/lessons',
          name: 'management-list-lesson',
          component: () => import('./views/management/lessons/lesson-list/ListLessons.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Lessons', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Lessons List',
            authRequired: true
          }
        },
        {
          path: '/a/lessons/:lessonId',
          name: 'management-view-lesson',
          component: () => import('./views/management/lessons/lesson-view/LessonView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Lessons', url: '/a/lessons'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Lesson',
            authRequired: true
          }
        },
        {
          path: '/a/lessons/:lessonId/builder',
          name: 'management-build-lesson',
          component: () => import('./views/management/lessons/lesson-edit/BuildLesson'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Lessons', url: '/a/lessons'},
              {title: 'Build', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Build Lesson',
            authRequired: true
          }
        },
        // =============================================================================
        // Term Management Routes
        // =============================================================================
        {
          path: '/a/terms',
          name: 'management-list-term',
          component: () => import('./views/management/terms/term-list/ListTerms.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Terms', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Terms Bank',
            authRequired: true
          }
        },
        {
          path: '/a/terms/upload',
          name: 'management-upload-term',
          component: () => import('./views/management/terms/term-create/UploadTerms'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Terms', url: '/a/terms'},
              {title: 'Upload', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Upload Terms',
            authRequired: true
          }
        },
        {
          path: '/a/terms/:termId/edit',
          name: 'management-edit-term',
          component: () => import('./views/management/terms/term-edit/EditTerm.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Terms', url: '/a/terms'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Term',
            authRequired: true
          }
        },
        // =============================================================================
        // Subscription Management Routes
        // =============================================================================
        {
          path: '/a/subscriptions',
          name: 'management-list-subscription',
          component: () => import('./views/management/subscriptions/subscription-list/ListSubscriptions'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'admin',
            pageTitle: 'Subscriptions List',
            authRequired: true
          }
        },
        {
          path: '/a/subscriptions/:subscriptionsId',
          name: 'management-view-subscription',
          component: () => import('./views/management/subscriptions/subscription-list/ListSubscriptions'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Subscriptions', url: '/a/subscriptions'},
              {title: 'View', active: true}
            ],
            rule: 'admin',
            pageTitle: 'View Subscription',
            authRequired: false
          }
        }
      ]
    },

    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/learn/start',
          name: 'learn-app',
          component: () => import('./views/learn/LearnRenderer'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        {
          path: '/activity/flashcards/start',
          name: 'flashcard-app',
          component: () => import('./views/activity/flashcard/FlashcardRenderer'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        {
          path: '/login',
          name: 'login',
          component: () => import('./views/login/Login.vue'),
          meta: {
            rule: 'visitor',
            authRequired: false
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          meta: {
            rule: 'visitor',
            authRequired: true
          },
          component: () => import('./views/utils/Error404.vue')
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})

router.beforeEach((to, from, next) => {

  //if (to.path === '/authorize') {
  // if (localStorage.getItem('accessToken') && localStorage.getItem('refreshToken')) {
  // }
  // return next()
  // }

  if (to.meta.authRequired) {

    if (!localStorage.getItem('refreshToken')) {
      return router.push({path: '/login'})
    }

    if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
      return router.push({path: '/login'})
    }
  }

  return next()

})

router.afterEach((to) => {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
  if (!to.path.includes('/login')) {
    store.dispatch('auth/refreshUserObject')
      .catch(() => {})

    store.dispatch('auth/refreshCourseObject')
      .catch(() => {})

  }
})
export default router
